import { WorkoutLayerDomain } from '@strength/domain';
import {
  HasWorkoutLayer,
  WorkoutLayerBuilder,
  AlternateGroupMutationData,
  ExerciseMetricMutationData,
  ExerciseMutationData,
  SetFullMutationData,
  SetMetricMutationData,
  SetMutationData,
  SupersetMutationData,
} from '@strength/domain-workout';
import { v4 } from 'uuid';
import { WorkoutLayerModelName } from './types';

/**
 * These functions are specific to the modified published workflow.
 * They differ from the draft workflow in that they do not need to ensure the sets metric is present.
 */

export function swapExerciseEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  modelName: WorkoutLayerModelName,
  workoutSectionId: string,
  previousWorkoutExerciseId: string,
  data: ExerciseMutationData,
  metrics: ExerciseMetricMutationData[],
  sets: SetFullMutationData[] = [],
  superset?: SupersetMutationData,
  alternateGroup?: AlternateGroupMutationData,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  // If athlete layer, remove the old exercise and add the new one
  // Else if the base layer, update the exercise
  const isAthleteLayer = [
    'AthleteActualWorkoutLayer',
    'AthleteDraftWorkoutLayer',
    'AthleteModifiedPublishedWorkoutLayer',
  ].includes(modelName);

  return new WorkoutLayerBuilder({
    workoutLayer: workoutLayer?.layer,
  })
    .swapExercise({
      replaceExercise: isAthleteLayer,
      workoutSectionId,
      previousWorkoutExerciseId,
      exerciseData: data,
      metrics: metrics,
      sets: sets,
      superset: superset,
      alternateGroup: alternateGroup,
      preserveIds: true,
    })
    .getEdits();
}

export function addExerciseMetricEdits(
  workoutExerciseId: string,
  data: ExerciseMetricMutationData,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  return new WorkoutLayerBuilder()
    .addExerciseMetrics({
      workoutExerciseId,
      metrics: [data],
      preserveIds: true,
    })
    .getEdits();
}

export function addSetEdits(
  workoutExerciseId: string,
  { sortOrder, workoutSetId, workoutSetGroupId }: SetMutationData,
  metrics: SetMetricMutationData[] = [],
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  return new WorkoutLayerBuilder()
    .addSets({
      workoutExerciseId,
      sets: [{ sortOrder, workoutSetId, workoutSetGroupId, metrics }],
      preserveIds: true,
    })
    .getEdits();
}

export function addExerciseEdits(
  workoutSectionId: string,
  data: ExerciseMutationData,
  metrics: ExerciseMetricMutationData[],
  sets: SetFullMutationData[] = [],
  altGroupId?: string,
  workoutSupersetId?: string,
  _workoutExerciseId?: string,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  const workoutExerciseId = _workoutExerciseId ?? v4();

  return new WorkoutLayerBuilder()
    .addExercise({
      workoutSectionId,
      workoutExerciseId,
      workoutAlternateGroupId: altGroupId,
      workoutSupersetId,
      data,
    })
    .addExerciseMetrics({ workoutExerciseId, metrics: metrics })
    .addSets({ workoutExerciseId, sets: sets })
    .getEdits();
}
